import { isId } from "@typedoc/contracts/ids";

const KEY = "abacor.pending-invitation";

/**
 * Carries the invitation through sign-in, sign-up and the six-digit verification, which are
 * three screens and a reload apart.
 *
 * The id in the link is the invitation's only credential, so it stays out of every URL that does
 * not have to hold it: once the visitor is signed in the page moves to a clean address and this
 * remembers which invitation it is. `sessionStorage` rather than `localStorage`, so it belongs to
 * one tab and does not outlive the window; every access is guarded because storage throws in a
 * private window, and a failure to remember only costs the visitor a trip back to their email.
 */
export function rememberPendingInvitation(id: string): void {
  try {
    sessionStorage.setItem(KEY, id);
  } catch {
    // Storage is unavailable. The link in the email still works as many times as it is opened.
  }
}

export function readPendingInvitation(): string | null {
  try {
    const id = sessionStorage.getItem(KEY);
    return id && isId("invitation", id) ? id : null;
  } catch {
    return null;
  }
}

export function forgetPendingInvitation(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear if it could never be written.
  }
}

/**
 * Where signing in or confirming an address leads: back to the invitation that was opened, or
 * home. The invitation page itself decides what to do with the person from there.
 */
export function destinationAfterSignIn(): { to: "/invite" } | { to: "/home" } {
  return readPendingInvitation() === null ? { to: "/home" } : { to: "/invite" };
}
